// Client-side session starring — a per-conversation favourite that lives ONLY
// in the browser's localStorage (no backend, no adapter call, nothing on the
// session record). Two keys:
//   - `sbui:starred-sessions`  → JSON array of starred session ids
//   - `sbui:starred-collapsed` → '1' when the Starred section is collapsed
// Both degrade silently when storage is unavailable (SSR, private mode, a
// sandboxed iframe whose `localStorage` access throws, quota) so the list
// always renders. Session ids live in storage only — never in rendered output
// (the ConversationList no-id/no-enum contract still holds).
import { useCallback, useEffect, useState } from 'react';
const STARRED_KEY = 'sbui:starred-sessions';
const COLLAPSED_KEY = 'sbui:starred-collapsed';
function safeStorage() {
    try {
        if (typeof window === 'undefined')
            return null;
        return window.localStorage ?? null;
    }
    catch {
        // Accessing `localStorage` itself can throw in a sandboxed iframe.
        return null;
    }
}
function readStarred() {
    const ls = safeStorage();
    if (ls === null)
        return new Set();
    try {
        const raw = ls.getItem(STARRED_KEY);
        if (raw === null)
            return new Set();
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed))
            return new Set();
        return new Set(parsed.filter((x) => typeof x === 'string'));
    }
    catch {
        return new Set();
    }
}
function writeStarred(ids) {
    const ls = safeStorage();
    if (ls === null)
        return;
    try {
        ls.setItem(STARRED_KEY, JSON.stringify([...ids]));
    }
    catch {
        // Quota / unavailable — starring is best-effort, never blocks the UI.
    }
}
export function useStarredSessions() {
    const [starred, setStarred] = useState(readStarred);
    // Cross-tab sync: a star toggled in another tab updates this list too.
    useEffect(() => {
        if (safeStorage() === null)
            return;
        const onStorage = (e) => {
            if (e.key === STARRED_KEY)
                setStarred(readStarred());
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);
    const toggle = useCallback((sessionId) => {
        setStarred((prev) => {
            const next = new Set(prev);
            if (next.has(sessionId))
                next.delete(sessionId);
            else
                next.add(sessionId);
            writeStarred(next);
            return next;
        });
    }, []);
    return { starred, toggle };
}
/** Collapsed state for the Starred section, persisted to localStorage. */
export function useStarredCollapsed() {
    const [collapsed, setCollapsed] = useState(() => {
        const ls = safeStorage();
        if (ls === null)
            return false;
        try {
            return ls.getItem(COLLAPSED_KEY) === '1';
        }
        catch {
            return false;
        }
    });
    const toggle = useCallback(() => {
        setCollapsed((prev) => {
            const next = !prev;
            const ls = safeStorage();
            if (ls !== null) {
                try {
                    ls.setItem(COLLAPSED_KEY, next ? '1' : '0');
                }
                catch {
                    // best-effort
                }
            }
            return next;
        });
    }, []);
    return [collapsed, toggle];
}
//# sourceMappingURL=use-starred-sessions.js.map